
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, arrayUnion } from 'firebase/firestore';
import moment from 'moment';
import { db } from '../lib/firebase';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Transfer.css';
import Loader from '../components/Loader';
import fiat from '../images/fiat.png';

const AddTransaction = () => {
    // State variables
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [amount, setAmount] = useState(0);
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
    const [type, setType] = useState('credit');
    const [account, setAccount] = useState('Investments');
    const [userBalance, setUserBalance] = useState(0);
    const [accountBalance, setAccountBalance] = useState(0);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [userInput, setUserInput] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);



    // * Fetch users from Firestore on component mount
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'stock'));
                const usersList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setUsers(usersList);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching users: ', error);
                setErrorMessage('Error fetching users.');
                setLoading(false);
            }
        };

        fetchUsers();
    }, []);

    // * Handle user change in the dropdown
    const handleUserChange = async (e) => {
        const userId = e.target.value;
        setSelectedUserId(userId);
        try {
            const userDoc = await getDoc(doc(db, 'stock', userId));
            if (userDoc.exists()) {
                const fetchedTransactions = userDoc.data().transactions || [];
                const calculatedBalance = fetchedTransactions.reduce((total, transaction) => {
                    // if transaction type is credit, add amount else subtract
                    return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
                }, 0);
                setUserBalance(calculatedBalance);

                const accountTransactions = fetchedTransactions.filter(txn => txn.account === account);
                const calculatedAccountBalance = accountTransactions.reduce((total, transaction) => {
                    return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
                }, 0);
                setAccountBalance(calculatedAccountBalance);
            }
        } catch (error) {
            console.error('Error fetching user balance: ', error);
            setErrorMessage('Error fetching user balance.');
        }
    };

    // * Handle account change
    const handleAccountChange = (e) => {
        setAccount(e.target.value);
        if (selectedUserId) {
            // Recalculate balance for the new account type
            handleUserChange({ target: { value: selectedUserId } });
        }
    };

    // * Handle all changes from user input
    const handleUserInputChange = (e) => {
        const input = e.target.value;
        setUserInput(input);

        if (input.trim() === '') {
            setFilteredUsers([]);
        } else {
            const filtered = users.filter(user =>
                user.fullName && user.fullName.toLowerCase().includes(input.toLowerCase()) // Check if fullName exists
            );
            setFilteredUsers(filtered);
        }
    };


    // * Handle the user selection
    const handleUserSelect = async (userId) => {
        setSelectedUserId(userId);
        setUserInput('');
        setFilteredUsers([]);
        try {
            const userDoc = await getDoc(doc(db, 'stock', userId));
            if (userDoc.exists()) {
                const transactions = userDoc.data().transactions || [];
                const calculatedBalance = transactions.reduce((total, txn) => {
                    return txn.type === 'credit' ? total + txn.amount : total - txn.amount;
                }, 0);
                setUserBalance(calculatedBalance);
            }
        } catch (error) {
            console.error('Error fetching user balance: ', error);
            setErrorMessage('Error fetching user balance.');
        }
    };

    // *go back buttons
    const handleGoBack = () => {
        navigate(-1);
    };

    // * Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Start submission state
        setFormSubmitting(true);
        // Clear previous errors
        setErrorMessage('');
        // Clear previous success messages
        setSuccessMessage('');

        // Validation
        if (!selectedUserId || !amount || !description || !date || !type || !account) {
            setErrorMessage('All fields are required.');
            setFormSubmitting(false);
            return;
        }

        const parsedAmount = parseFloat(amount);
        if (parsedAmount <= 0) {
            setErrorMessage('Amount must be greater than 0.');
            setFormSubmitting(false);
            return;
        }

        if (type === 'debit' && userBalance < parsedAmount) {
            setErrorMessage('Insufficient balance for a debit transaction.');
            setFormSubmitting(false);
            return;
        }

        try {
            // Create the transaction object
            const transaction = {
                amount: parsedAmount,
                description,
                // Human-readable format
                date: moment(date).format('MMMM D, YYYY'),
                type,
                beneficiary: "CBC",
                account,
            };

            // Update the user's transactions in Firestore
            await updateDoc(doc(db, 'stock', selectedUserId), {
                transactions: arrayUnion(transaction),
            });

            // Update local state for balance
            const newBalance = type === 'credit' ? userBalance + parsedAmount : userBalance - parsedAmount;
            setUserBalance(newBalance);

            const newAccountBalance = type === 'credit' ? accountBalance + parsedAmount : accountBalance - parsedAmount;
            setAccountBalance(newAccountBalance);

            // Reset form fields
            setAmount(0);
            setDescription('');
            // Reset to today's date
            setDate(moment().format('YYYY-MM-DD'));
            setType('credit');

            // Display success message
            setSuccessMessage('Transaction added successfully! Click here to view the transaction.');
        } catch (error) {
            console.error('Error adding transaction:', error);
            setErrorMessage('Error adding transaction. Please try again.');
        } finally {
            // End submission state
            setFormSubmitting(false);
        }
    };


    if (loading) {
        return <div><Loader /></div>;
    }

    if (errorMessage) {
        return <div>{errorMessage}</div>;
    }

    return (
        <div className="add-transaction" id='transfer'>
            <h2>Add Transaction</h2>
            <form onSubmit={handleSubmit}>

                <div className='center'>
                    {selectedUserId && (
                        <div className="selected-user-info user-info-container">
                            <div className="users-info-item">
                                <img
                                    src={users.find(user => user.id === selectedUserId)?.profilePictureUrl || fiat}
                                    alt="User DP"
                                    className='selected-profile-picture'
                                />
                            </div>
                            <div className="users-info-items Link">
                                <p><strong>Selected User:</strong> {users.find(user => user.id === selectedUserId)?.fullName}</p>
                                <p><strong>Current Balance:</strong> ${userBalance.toFixed(2)}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* User, Type, and Account fields */}
                <div className="container">
                    <div className="form-cover">
                        <div>
                            <label className="transaction-head">User:</label>
                            <input
                                type="text"
                                className="input-field"
                                placeholder="Search for a user"
                                value={userInput}
                                onChange={handleUserInputChange}
                            />
                            {filteredUsers.length > 0 && (
                                <ul className="user-dropdown">
                                    {filteredUsers.map(user => (
                                        <li
                                            key={user.id}
                                            onClick={() => handleUserSelect(user.id)}
                                            className="user-item"
                                        >
                                            {user.fullName}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        <div>
                            <label className='transaction-head'>Type:</label>
                            <select
                                value={type}
                                className='input-field'
                                onChange={(e) => setType(e.target.value)}
                                required
                            >
                                <option value="credit">Credit</option>
                                <option value="debit">Debit</option>
                            </select>
                        </div>

                        <div>
                            <label className='transaction-head'>Account:</label>
                            <input
                                value={account}
                                className='input-field'
                                onChange={handleAccountChange}
                                required
                                readOnly
                           />
                             
                        </div>
                    </div>
                </div>

                {/* Amount and Date fields */}
                <div className="container">
                    <div className="form-cover">
                        <div>
                            <label className='transaction-head'>Amount:</label>
                            <input
                                type="number"
                                className='input-field'
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                required
                                min="0.01"
                                step="0.01"
                            />
                        </div>

                        <div>
                            <label className='transaction-head'>Date (MM/DD/YYYY):</label>
                            <input
                                type="date"
                                value={date}
                                className='input-field'
                                onChange={(e) => setDate(e.target.value)}
                                required
                            />
                        </div>

                        {/* Description field */}
                        <div>
                            <label className='transaction-head'>Description:</label>
                            <input
                                type="text"
                                value={description}
                                className='input-field'
                                placeholder='Example: This is for Clothes.'
                                onChange={(e) => setDescription(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Submit button */}
                <button className="add-transaction-btn" type="submit" disabled={type === 'debit' && userBalance <= 0}>
                    {formSubmitting ? 'Processing...' : 'Add Transaction'}
                </button>
            </form>


            {/* Success and error messages */}
            {successMessage && <p className="success-message">
                <Link className="Link" to={`/userdetail/${selectedUserId}/transactions`}>{successMessage}</Link>
            </p>}
            {errorMessage && <p className="error-message">{errorMessage}</p>}

            <div className="center">
                <button onClick={handleGoBack} className='click-button'>Go Back</button>
            </div>
        </div>
    );
};

export default AddTransaction;